// Autosave.
//
// ---------------------------------------------------------------------------
// The single most important behaviour in this application
// ---------------------------------------------------------------------------
//
// A client sits down with a shoebox of receipts and works through a long form.
// If we lose their typing even once, they will not trust the thing again and
// will go back to the Word document. So this hook is built around one rule:
//
//     NEVER LOSE A CHANGE.
//
// Everything below follows from that:
//
//   * Changes go into a pending map immediately, synchronously, before any
//     network call. The source of truth for "what has the client told us" is
//     local, not the server's response.
//
//   * A failed save puts its changes BACK into the pending map, merged UNDER
//     anything typed since — so a retry cannot resurrect an old value over a
//     newer one.
//
//   * Only one request is in flight at a time. Overlapping saves can complete
//     out of order, and the loser silently wins.
//
//   * A page being hidden (tab switch, phone locking, closing the browser)
//     flushes immediately via sendBeacon, which outlives the page where a
//     normal fetch would be cancelled.
//
//   * Retries back off but never give up while the page is open. Someone on a
//     train through a tunnel should find their work saved when they come out.

import type { AnswerValue } from '@myshoebox/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../api/client.js';

/** How long after the last keystroke to save. */
const DEBOUNCE_MS = 800;

/** Backoff between failed attempts, then every 15s indefinitely. */
const RETRY_DELAYS_MS = [1_000, 2_000, 5_000, 10_000, 15_000];

export type SaveState =
  | { status: 'idle' }
  | { status: 'pending' } // changes queued, not yet sent
  | { status: 'saving' }
  | { status: 'saved'; at: Date }
  | { status: 'retrying'; attempt: number; message: string }
  | { status: 'error'; message: string }; // not retryable — needs the user

export interface UseAutosaveOptions {
  /** Persist a batch. Must reject on failure so the batch can be requeued. */
  save: (patch: Record<string, AnswerValue>) => Promise<void>;
  /**
   * URL for the last-ditch beacon flush when the page is hidden.
   * Omit to disable; the hook still flushes on `pagehide` via a normal save
   * attempt, which usually but not always completes.
   */
  beaconUrl?: string;
}

export interface UseAutosaveResult {
  /** Queue a change. Returns immediately; the save happens later. */
  queue: (code: string, value: AnswerValue) => void;
  /** Force a save now and wait for it. Used before submitting or navigating. */
  flush: () => Promise<void>;
  state: SaveState;
  /** True while anything is unsaved — drives the "unsaved changes" guard. */
  hasPending: boolean;
}

export function useAutosave({ save, beaconUrl }: UseAutosaveOptions): UseAutosaveResult {
  const [state, setState] = useState<SaveState>({ status: 'idle' });
  const [hasPending, setHasPending] = useState(false);

  // Refs rather than state: these are mutated from timers and async callbacks
  // where a stale closure over state would lose changes.
  const pending = useRef<Record<string, AnswerValue>>({});
  const inFlight = useRef(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const attempt = useRef(0);
  // Keep the latest save function without making flush() change identity on
  // every render, which would restart the effects below.
  const saveRef = useRef(save);
  saveRef.current = save;

  const clearTimers = useCallback(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (retryTimer.current) clearTimeout(retryTimer.current);
    debounceTimer.current = undefined;
    retryTimer.current = undefined;
  }, []);

  /**
   * Send whatever is pending.
   *
   * Takes the whole pending map and empties it, so changes made DURING the
   * request accumulate for the next round rather than being lost or sent
   * twice.
   */
  const send = useCallback(async (): Promise<void> => {
    if (inFlight.current) return;

    const batch = pending.current;
    if (Object.keys(batch).length === 0) {
      setHasPending(false);
      return;
    }

    pending.current = {};
    inFlight.current = true;
    setState({ status: 'saving' });

    try {
      await saveRef.current(batch);
      attempt.current = 0;
      inFlight.current = false;

      // Anything typed while that request was in flight.
      if (Object.keys(pending.current).length > 0) {
        setHasPending(true);
        void send();
      } else {
        setHasPending(false);
        setState({ status: 'saved', at: new Date() });
      }
    } catch (error) {
      inFlight.current = false;

      // Put the batch BACK, merged UNDERNEATH anything typed since. A retry
      // must never overwrite a newer value with the one that failed.
      pending.current = { ...batch, ...pending.current };
      setHasPending(true);

      const apiError = error instanceof ApiError ? error : undefined;

      if (apiError && !apiError.isRetryable) {
        // A 400 means the request itself was wrong. Retrying forever would
        // spin and never succeed, so surface it.
        setState({ status: 'error', message: apiError.message });
        return;
      }

      const delay =
        RETRY_DELAYS_MS[Math.min(attempt.current, RETRY_DELAYS_MS.length - 1)] ?? 15_000;
      attempt.current += 1;
      setState({
        status: 'retrying',
        attempt: attempt.current,
        message: apiError?.message ?? 'Could not save.',
      });

      retryTimer.current = setTimeout(() => void send(), delay);
    }
  }, []);

  const queue = useCallback(
    (code: string, value: AnswerValue) => {
      pending.current[code] = value;
      setHasPending(true);
      setState((current) => (current.status === 'saving' ? current : { status: 'pending' }));

      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => void send(), DEBOUNCE_MS);
    },
    [send],
  );

  const flush = useCallback(async () => {
    clearTimers();
    await send();
  }, [clearTimers, send]);

  // -------------------------------------------------------------------------
  // Flush when the page goes away
  // -------------------------------------------------------------------------
  //
  // `visibilitychange` to hidden covers switching tabs and a phone locking;
  // `pagehide` covers closing and navigating away. Both matter: a client who
  // locks their phone mid-sentence should not lose it.
  //
  // sendBeacon is used because the browser cancels in-flight fetches when a
  // page unloads, and a beacon is guaranteed to be attempted.
  useEffect(() => {
    const flushWithBeacon = (): void => {
      const batch = pending.current;
      if (Object.keys(batch).length === 0) return;

      if (beaconUrl && typeof navigator.sendBeacon === 'function') {
        const sent = navigator.sendBeacon(
          beaconUrl,
          new Blob([JSON.stringify({ answers: batch })], { type: 'application/json' }),
        );
        // Only clear if the browser accepted it. If it refused (payload too
        // large, for instance), keep the changes so a normal save still tries.
        if (sent) {
          pending.current = {};
          setHasPending(false);
        }
      }
    };

    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'hidden') flushWithBeacon();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', flushWithBeacon);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', flushWithBeacon);
    };
  }, [beaconUrl]);

  // -------------------------------------------------------------------------
  // Warn before leaving with unsaved changes
  // -------------------------------------------------------------------------
  //
  // A belt-and-braces backstop only. The beacon above should mean this never
  // fires, but if a save is genuinely failing the client deserves to be told
  // rather than silently losing work.
  useEffect(() => {
    if (!hasPending) return;
    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasPending]);

  // Retry as soon as the connection returns, rather than waiting out the
  // backoff. Someone emerging from a tunnel sees "Saved" almost immediately.
  useEffect(() => {
    const onOnline = (): void => {
      if (Object.keys(pending.current).length > 0) {
        attempt.current = 0;
        void send();
      }
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [send]);

  // Flush on unmount so navigating between years cannot drop a change.
  useEffect(() => clearTimers, [clearTimers]);

  return { queue, flush, state, hasPending };
}

/** Short status text for the header. */
export function saveStateLabel(state: SaveState): string {
  switch (state.status) {
    case 'idle':
      return '';
    case 'pending':
      return 'Saving…';
    case 'saving':
      return 'Saving…';
    case 'saved':
      return 'Saved';
    case 'retrying':
      return 'Reconnecting…';
    case 'error':
      return 'Not saved';
    default: {
      const exhaustive: never = state;
      return String(exhaustive);
    }
  }
}
